import { useEffect, useRef, useState } from 'react'
import { useCopy, useLocaleDescriptor } from '@/i18n/useCopy'
import { useSessionStore } from '@/store/sessionStore'
import { useStatusLabel } from '@/voice/useStatusLabel'
import { useWaitingNote } from '@/components/Waiting/useWaitingNote'
import { describeVoiceError } from '@/voice/describeError'

/**
 * Everything a screen reader is told about the conversation, in one place.
 *
 * Three regions, each with a job, and no overlap between them. Overlap is the
 * usual failure: a transcript announced twice, a button whose label and whose
 * live region both report the same state change, a partial hypothesis
 * re-reading the whole sentence on every word.
 *
 *   role="status"  the session state, in words, or the note about a wait that
 *                  has outlasted it. Polite and atomic.
 *   role="log"     finished turns only. Polite, not atomic, additions only.
 *   role="alert"   errors. No aria-live alongside it: VoiceOver on iOS speaks
 *                  a node with both twice.
 *
 * All three are rendered empty on mount and filled later. A live region that
 * appears with its message already inside it is frequently not announced at
 * all, which is the single most common bug in this area.
 *
 * Interim transcripts deliberately never reach the log. A hypothesis that
 * grows word by word would re-announce the entire sentence on every update,
 * because the default `aria-relevant` includes text changes. The visible
 * captions show partials; only finished turns are spoken.
 */
export function LiveRegions() {
  const copy = useCopy()
  const locale = useLocaleDescriptor()
  const statusLabel = useStatusLabel()
  const waitingNote = useWaitingNote()
  const turns = useSessionStore((state) => state.turns)
  const error = useSessionStore((state) => state.error)

  const [mounted, setMounted] = useState(false)
  const [announcedStatus, setAnnouncedStatus] = useState('')
  const statusTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Insert the regions on one tick, fill them on another.
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // States can change several times a second during a fast exchange. Announcing
  // every one would flood the queue, and screen readers do not coalesce
  // bursts, they read all of them in order, long after the fact.
  useEffect(() => {
    if (!mounted) return
    if (statusTimer.current !== null) clearTimeout(statusTimer.current)
    statusTimer.current = setTimeout(() => setAnnouncedStatus(statusLabel), 320)
    return () => {
      if (statusTimer.current !== null) clearTimeout(statusTimer.current)
    }
  }, [statusLabel, mounted])

  const lastTurn = turns.at(-1)

  return (
    <>
      {/*
        Past five seconds the wait itself is the more useful description of the
        moment, so it wins over the plain state: "Namu is still thinking" says
        everything "Thinking" does and also answers the question the silence has
        started to raise. It needs no debounce of its own, because it only ever
        changes at the five and twenty second marks.
      */}
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {mounted ? waitingNote || announcedStatus : ''}
      </div>

      <div
        className="visually-hidden"
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-relevant="additions"
        aria-label={copy.ui.a11y.liveTranscript}
      >
        {mounted && lastTurn ? (
          <p key={lastTurn.id}>
            {lastTurn.speaker === 'namu' ? copy.ui.common.namu : copy.ui.common.you}: {lastTurn.text}
          </p>
        ) : null}
      </div>

      {/*
        Announced in the user's language, from the same source as the visible
        banner. `error.message` is an English developer string; feeding it to a
        Hausa speech engine inside a document declared `lang="ha"` was the most
        common thing a Hausa screen-reader user heard, because no browser ships
        a Hausa voice and that notice fires at almost every session start.
      */}
      <div className="visually-hidden" role="alert">
        {mounted && error ? describeVoiceError(error.code, copy, locale).body : ''}
      </div>
    </>
  )
}
